export type ServiceInfoContent = {
  title: string;
  subtitle?: string;
  description: string[];
  features?: string[];
};

export const SERVICE_INFO_CONTENT: Record<string, ServiceInfoContent> = {
  maps: {
    title: "Maps",
    subtitle:
      "Browse AI2Peat's published GIS layers of Ireland's peatlands, built from satellite imagery and environmental data.",
    description: [
      "Explore ready-made peatland map layers, from ecotope classifications to satellite-derived land cover, directly in your browser, no download or GIS software required.",
      "Pan and zoom over a live basemap, toggle layers on and off, inspect legends, and click features for more detail. Layers are published from AI2Peat's processing pipelines and kept up to date as new data becomes available.",
    ],
    features: [
      "Interactive pan/zoom map viewer with a satellite or topographic basemap",
      "Toggleable GIS layers with legends",
      "Click-to-inspect feature details and metadata",
      "Access to publicly available AI2Peat map layers",
    ],
  },

  "specific-yield": {
    title: "Specific Yield Analysis",
    subtitle:
      "Turn your rainfall and dipwell CSV files into a clear, interactive hydrology report.",
    description: [
      "The Specific Yield Analysis service takes daily rainfall data and water-table depth (WTD) logger data from your site and runs an event-based Volumetric Water Balance (VWB) workflow. It estimates a single, representative Specific Yield (SY) value for your selected time window and shows how rainfall and water levels evolve through time.",
      "This service does not guess storage volumes or attempt full water-balance closure. It focuses on doing one job well: finding suitable rainfall events, measuring the dipwell response, and deriving a conservative SY estimate using a documented, repeatable method.",
    ],
    features: [
      "Daily rainfall CSV (Met station or on-site gauge)",
      "One or more dipwell CSVs (water-table depth over time)",
      "Interactive HTML report with rainfall + dipwell time-series",
      "Estimated Specific Yield (Sy) from rainfall–response events",
      "Downloadable machine-readable CSV outputs",
    ],
  },

  "pfi-scorecard": {
    title: "Emission Scoping Calculator",
    subtitle: "Indicative emission and carbon-balance scoring for peatland projects.",
    description: [
      "The Emission Scoping Calculator service provides a structured, area-of-interest-based assessment of greenhouse gas emissions and carbon balance across baseline and restored peatland conditions. It uses published emission factors mapped to peatland ecology (ecotopes) to estimate annual CO₂ emissions, reductions, and net ecosystem carbon balance (NECB).",
      "Users specify an area of interest, then the service leverages AI2Peat's peatland ecological maps to estimate emissions and calculates projected changes in global warming potential (GWP AR6) and NECB for that area.",
    ],
    features: [
      "Structured, standards-informed GHG reporting",
      "Area-of-interest-based inputs",
      "Baseline GHG emission estimation (GWP (AR6) and NECB)",
      "Downloadable scorecard tables for reporting and audits",
    ],
  },

  sentinel: {
    title: "Sentinel-2 On-Demand Imagery",
    subtitle: "Clean, science-ready Sentinel-2 imagery for your exact site and dates.",
    description: [
      "Get a consistent Sentinel-2 satellite image mosaic tailored specifically to your peatland area of interest. You define the site boundary, date range, and cloud cover threshold — we handle everything else.",
      "Behind the scenes, we query all available Sentinel-2 scenes over your area, filter out images exceeding the cloud threshold, clip imagery to your site boundary, and merge the results into a multi-band GeoTIFF containing standard Sentinel-2 bands (B1–B12), including NIR and SWIR bands commonly used in peatland analysis.",
    ],
    features: [
      "Multi-band GeoTIFF mosaic (B1–B12), clipped to your site and ready for QGIS, ArcGIS, or Python/R workflows",
      "Quick-look RGB preview images (PNG) for rapid visual validation",
      "Browser-viewable map preview (HTML) allowing pan and zoom over a basemap",
      "Simple technical summary including number of images used and area covered",
    ],
  },

  "safe-to-geotiff": {
    title: "Sentinel-2 SAFE to GeoTIFF Converter",
    subtitle:
      "Convert Sentinel-2 SAFE products into clean, analysis-ready GeoTIFF datasets suitable for GIS and scientific workflows.",
    description: [
      "This service extracts the available spectral bands from selected Sentinel-2 scenes, aligns them to a common reference band, and generates a compressed multi-band GeoTIFF that can be used directly in tools such as QGIS, ArcGIS, Python, or R.",
      "Instead of working with the complex SAFE file structure, you simply select the scenes you want from the catalog and the system handles the band extraction, stacking, and formatting automatically.",
    ],
    features: [
      "Multi-band GeoTIFF containing aligned Sentinel-2 spectral bands",
      "Optimized compressed output for efficient storage and fast loading",
      "Processing metadata and summary for traceability and reproducibility",
    ],
  },

  "pixel-extraction": {
    title: "Sentinel-2 Pixel Extraction",
    subtitle:
      "Turn your imagery and polygons into a ready-to-train dataset for ecotope or land-cover models.",
    description: [
      "Convert a Sentinel-2 GeoTIFF mosaic and a labelled polygon layer into a structured, machine-learning-ready training dataset. You provide the raster and class polygons — we handle the extraction and formatting.",
      "Behind the scenes, polygons are automatically aligned to the raster grid, CRS differences are resolved, and the raster is processed in memory-efficient windows, ensuring consistent band ordering and clean column naming across runs.",
    ],
    features: [
      "Training dataset file (Parquet by default, CSV optional) with one row per pixel",
      "Columns for all raster bands plus your chosen label fields (e.g. class, ecotope, bog_type)",
      "Pixel coordinates (x, y) and polygon ID included for full traceability",
      "Extraction summary with total pixels, polygons represented, and key processing parameters",
    ],
  },

  "pixel-annotation": {
    title: "Pixel Annotation & Cloud Masking",
    subtitle:
      "Draw straight on your Sentinel-2 imagery to label land cover or paint out clouds.",
    description: [
      "Upload a file and it renders directly on a satellite map — RGB imagery, and the model's land-cover prediction if the file has one, blendable with a single opacity control.",
      "In Label Annotation mode, draw rectangles, polygons, or freehand shapes and give each one a label, then export the set as a JSON file or a zipped ESRI Shapefile. In Cloud Mask mode, paint over cloud or cloud-shadow pixels with a zoom-aware brush, and save straight back to a copy of your file with the mask appended as a new band or column (1 = cloud, 0 = clear).",
    ],
    features: [
      "Upload a parquet (row/col/x/y/band_*, optional predicted_class) or a stacked GeoTIFF",
      "Rectangle, polygon, and freehand drawing tools with per-shape labels",
      "Export labelled shapes as JSON or a zipped ESRI Shapefile",
      "Cloud-mask brush with zoom-aware sizing, saved back as a banded GeoTIFF or parquet column",
    ],
  },

  "sentinel2-wrapper": {
    title: "Sentinel-2 Wrapper",
    subtitle:
      "Create machine-learning-ready Sentinel-2 training datasets from site boundaries and labelled polygons in one guided workflow.",
    description: [
      "This service combines Sentinel-2 scene discovery, SAFE validation, GeoTIFF conversion, and pixel extraction into a single end-to-end pipeline. You provide your area of interest, labelled training polygons, date range, and cloud threshold, and the system finds suitable scenes, prepares previews for review, then generates ready-to-train pixel datasets after approval.",
      "Instead of manually downloading SAFE files, converting bands, checking scenes, and extracting pixels in separate tools, the pipeline manages the full process automatically while keeping user validation in the loop.",
    ],
    features: [
      "Interactive Sentinel-2 tile preview with RGB view, map zoom, and validation step before processing",
      "Converted multi-band GeoTIFF generated from approved Sentinel-2 SAFE scenes",
      "Machine-learning-ready pixel dataset in Parquet format",
      "Final ZIP package containing outputs, summary, metadata, and processing logs",
    ],
  },

  "landcover-inference": {
    title: "Model Inference",
    subtitle:
      "Upload a Sentinel-2 pixel parquet file, select a model, and get labelled predictions in minutes.",
    description: [
      "Upload your pixel-level parquet file (band_1–band_12 required), pick a registered MLflow model, and the pipeline runs inference and returns a downloadable ZIP with your results. Currently available models include Landcover Mapping and several trained ecotope/habitat classification models, with more added in future updates.",
    ],
    features: [
      "predicted.parquet with input columns plus predicted class, class index, and per-class probabilities",
      "GeoTIFF raster of predicted classes ready for QGIS and other GIS tools",
      "Interactive HTML map preview for quick spatial inspection of results",
      "Everything packaged as a single downloadable ZIP",
    ],
  },

  "tif-upload": {
    title: "Upload TIF to GCS",
    subtitle:
      "Upload a locally-generated GeoTIFF directly to the shared GCS bucket so it can be reviewed and published to the Peatsense map.",
    description: [
      "Data scientists who run inference models locally can use this tool to bring their results into the platform. Files go directly from your browser to GCS, with no size bottleneck through the server. Once uploaded, you can optionally publish the layer straight to GeoServer, or leave it for a staff member to review and publish later.",
    ],
    features: [
      "Drag and drop your .tif or .tiff file (up to 5 GB)",
      "Set a display name and optional description",
      "File uploads directly to the shared GCS bucket under manual_uploads/",
      "Optionally publish to GeoServer immediately, or let staff do it later",
    ],
  },

  "parquet-viewer": {
    title: "Files Reader",
    subtitle:
      "Explore Parquet and CSV files directly in the browser. No data is ever saved to disk.",
    description: [
      "Upload any Parquet or CSV file and immediately browse its schema and rows. The file is parsed in server memory and discarded when you close the viewer. Supports all Parquet codecs (Snappy, GZIP, ZSTD, LZ4, Brotli) and CSV files with auto-detected delimiters.",
    ],
    features: [
      "Drag and drop or click to upload a .parquet or .csv file",
      "Browse the full schema, column names, types, and nullability",
      "Paginated row view (200 rows per page) with full-text search",
      "Sort any column ascending or descending",
    ],
  },

  "data-catalog": {
    title: "Data Catalog",
    subtitle:
      "Browse every asset produced or consumed across AI2Peat's services, in one searchable table.",
    description: [
      "Every input file and output artifact from the on-demand services, including satellite imagery, uploaded shapefiles, extracted pixel datasets, and trained models, is tracked as an entry in the data catalog. Use this view to look up which service produced or reused a given asset, who requested it, and when.",
    ],
    features: [
      "Full list of tracked asset usages across all services",
      "Search by asset name, service, request ID, or user",
      "See whether an asset was newly created or reused from an existing entry",
      "Expand any row to see full asset details, including storage locations and file info",
    ]
  },

  "multitemp-vis": {
    title: "Multitemporal Visualisation",
    subtitle:
      "Browse AI2Peat's published GIS layers of Irish Peatlands over time, built with satellite imagery and environmental data.",
    description: [
      "Explore ready-made peatland map layers — directly in your browser, no download or GIS software required.",
      "View GIS layers in an illustrative grid to quickly compare layers observed at different sensing times. Furthermore, view them on the map view and browse between sensing dates",
    ],
    features: [
      "Snappy grid view of an area of interest over time",
      "Slideshow of the layers over the area of interest on the map",
    ],
  },
};
